import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@verlo/shared/components/ui/button';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Signal } from '@verlo/shared/components/ui/signal';
import { promotionsApi } from '@verlo/shared/services/api';
import { useApp } from '@verlo/shared/contexts/AppContext';
import { KINDS } from '@verlo/shared/lib/promotions';
import { Download } from 'lucide-react';
import { useSite } from '../../../contexts/SiteContext';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { PageHeader, Stat, Stats, DataTable, FilterBar, Empty, Insight } from '../../../components/office';

/**
 * Reports › Promotions (promotions plan, wave 2). Carl's questions in order: did it
 * work, over time, by rate, by item (PLU), by seller, by site. Every figure is read from
 * the record the till wrote on the line; the one derived figure — uplift — says what it
 * compared against, or that it couldn't.
 */
const RANGES = [
  { value: 'last_7', label: 'Last 7 days' }, { value: 'last_30', label: 'Last 30 days' },
  { value: 'last_90', label: 'Last 90 days' }, { value: 'mtd', label: 'This month' },
];

const SECTION = 'mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint';

/** Redemptions per day as bars; a day the offer ran is inked, a day it didn't is faint. */
const DailyBars = ({ daily = [], focus }) => {
  const W = 760; const H = 96; const n = daily.length || 1;
  const bw = Math.max(2, Math.floor(W / n) - 2);
  const max = Math.max(1, ...daily.map((d) => d.redemptions));
  return (
    <svg viewBox={`0 0 ${W} ${H + 18}`} className="w-full" role="img" aria-label="Redemptions per day">
      {daily.map((d, i) => {
        const h = Math.round((d.redemptions / max) * H);
        const ran = focus ? d.ran.includes(focus) : d.ran.length > 0;
        return (
          <g key={d.date}>
            <rect x={i * (bw + 2)} y={H - h} width={bw} height={h} className={ran ? 'fill-verlo' : 'fill-hairline-strong'} />
            {!ran && <rect x={i * (bw + 2)} y={H - 2} width={bw} height={2} className="fill-hairline" />}
            {(i === 0 || i === n - 1 || (n > 14 && i % 7 === 0)) && (
              <text x={i * (bw + 2)} y={H + 14} className="fill-ink-faint" fontSize="10">{d.date.slice(5)}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const csvOf = (rows, cols) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.map((c) => esc(c.label)).join(','), ...rows.map((r) => cols.map((c) => esc(typeof c.get === 'function' ? c.get(r) : r[c.key])).join(','))].join('\n');
};

const PromotionsReportPage = () => {
  const { formatCurrency } = useApp();
  const { site } = useSite();
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState(params.get('range') || 'last_30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const focus = params.get('offer') || null;

  useEffect(() => {
    setLoading(true);
    promotionsApi.report({ range, ...(site ? { site } : {}) })
      .then(({ data: d }) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [range, site]);

  const t = data?.totals || {};
  const offers = useMemo(() => (data?.offers || []).filter((o) => !focus || o.id === focus), [data, focus]);
  const focused = focus ? offers[0] : null;
  const kindLabel = (k) => KINDS.find((x) => x.value === k)?.label || k;

  const offerColumns = [
    { key: 'name', label: 'Offer', sortable: true, render: (r) => (
      <div className="min-w-0">
        <button type="button" className="font-semibold text-ink hover:text-verlo-hover text-left" onClick={() => setParams({ offer: r.id, range })}>{r.name}</button>
        <p className="text-[12px] text-ink-soft truncate">{r.words}{r.kind !== 'site' ? ` · ${kindLabel(r.kind)}${r.supplier ? ` · ${r.supplier}` : ''}` : ''}</p>
      </div>
    ) },
    { key: 'redemptions', label: 'Used', align: 'right', mono: true, sortable: true, render: (r) => r.redemptions ? <span>{r.redemptions}<span className="text-ink-faint"> · {r.units} units</span></span> : <span className="text-ink-faint">never</span> },
    { key: 'days_ran', label: 'Ran', align: 'right', mono: true, sortable: true, render: (r) => <span className="text-ink-soft">{r.days_ran}<span className="text-ink-faint">/{r.days_in_range} days</span></span> },
    { key: 'share_of_orders', label: 'Of orders', align: 'right', mono: true, sortable: true, render: (r) => `${r.share_of_orders}%` },
    { key: 'given', label: 'Given', align: 'right', mono: true, sortable: true, render: (r) => formatCurrency(r.given) },
    { key: 'net_cost', label: 'Net cost', align: 'right', mono: true, sortable: true, render: (r) => r.funded ? <span>{formatCurrency(r.net_cost)}<span className="text-ink-faint"> · {formatCurrency(r.funded)} funded</span></span> : formatCurrency(r.net_cost) },
    { key: 'uplift', label: 'Uplift', align: 'right', render: (r) => r.uplift?.pct != null
      ? <Signal tone={r.uplift.pct >= 0 ? 'ok' : 'warn'} dot={false}>{r.uplift.pct >= 0 ? '+' : ''}{r.uplift.pct}%</Signal>
      : <span className="text-[12px] text-ink-faint" title={r.uplift?.note}>no comparison</span> },
  ];
  const rateColumns = [
    { key: 'rate', label: 'Rate', render: (r) => <span className="font-semibold text-ink">{r.rate}</span> },
    { key: 'redemptions', label: 'Used', align: 'right', mono: true, sortable: true },
    { key: 'units', label: 'Units', align: 'right', mono: true, sortable: true },
    { key: 'given', label: 'Given', align: 'right', mono: true, sortable: true, render: (r) => formatCurrency(r.given) },
    { key: 'offers', label: 'Offers', render: (r) => <span className="text-ink-soft">{(r.offers || []).join(', ')}</span> },
  ];
  const handColumns = [
    { key: 'rate', label: 'Hand discount', render: (r) => <span className="font-semibold text-ink">{r.rate}</span> },
    { key: 'count', label: 'Times', align: 'right', mono: true, sortable: true },
    { key: 'given', label: 'Given', align: 'right', mono: true, sortable: true, render: (r) => formatCurrency(r.given) },
  ];
  const itemColumns = [
    { key: 'plu', label: 'PLU', mono: true, sortable: true, render: (r) => <span className="text-ink-soft tabular-nums">{r.plu ?? '—'}</span> },
    { key: 'name', label: 'Item', sortable: true, render: (r) => <span className="font-semibold text-ink">{r.name}</span> },
    { key: 'units_on', label: 'On offer', align: 'right', mono: true, sortable: true },
    { key: 'units_off', label: 'Off offer', align: 'right', mono: true, sortable: true },
    { key: 'asp_on', label: 'ASP on', align: 'right', mono: true, render: (r) => r.asp_on != null ? formatCurrency(r.asp_on) : '—' },
    { key: 'asp_off', label: 'ASP off', align: 'right', mono: true, render: (r) => r.asp_off != null ? formatCurrency(r.asp_off) : '—' },
    { key: 'given', label: 'Given', align: 'right', mono: true, sortable: true, render: (r) => formatCurrency(r.given) },
    { key: 'offers', label: 'Offers', render: (r) => <span className="text-ink-soft">{(r.offers || []).join(', ')}</span> },
  ];
  const sellerColumns = [
    { key: 'name', label: 'Sold by', render: (r) => <span className="font-semibold text-ink">{r.name}</span> },
    { key: 'units', label: 'Units', align: 'right', mono: true, sortable: true },
    { key: 'redemptions', label: 'Bills', align: 'right', mono: true, sortable: true },
    { key: 'given', label: 'Given', align: 'right', mono: true, sortable: true, render: (r) => formatCurrency(r.given) },
  ];
  const siteColumns = [
    { key: 'name', label: 'Site', render: (r) => <span className="font-semibold text-ink">{r.name}</span> },
    { key: 'redemptions', label: 'Used', align: 'right', mono: true, sortable: true },
    { key: 'units', label: 'Units', align: 'right', mono: true, sortable: true },
    { key: 'given', label: 'Given', align: 'right', mono: true, sortable: true, render: (r) => formatCurrency(r.given) },
  ];

  const downloadItems = () => {
    const cols = [{ key: 'plu', label: 'PLU' }, { key: 'name', label: 'Item' }, { key: 'category', label: 'Section' }, { key: 'units_on', label: 'Units on offer' }, { key: 'units_off', label: 'Units off offer' }, { key: 'asp_on', label: 'ASP on' }, { key: 'asp_off', label: 'ASP off' }, { key: 'given', label: 'Given' }, { key: 'offers', label: 'Offers', get: (r) => (r.offers || []).join('; ') }];
    const blob = new Blob([csvOf(data?.by_item || [], cols)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `promotions-by-item-${data?.start}-${data?.end}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const sellers = focused ? focused.sellers : (data?.by_seller || []);
  const sites = focused ? focused.sites : (data?.by_site || []);

  return (
    <ReportsLayout>
      <PageHeader
        title="Promotions"
        purpose="Did it work? Which offer, which rate, which item, who sold it, and what it cost after the supplier's share."
        actions={<Link to="/back-office/promotions" className="text-[13px] text-verlo-hover hover:underline">Set up offers →</Link>}
        testId="promotions-report-header"
      />
      <FilterBar right={<span>{data ? `${data.start} – ${data.end} · ${t.orders ?? 0} orders` : '—'}</span>}>
        <Segmented value={range} onChange={(v) => { setRange(v); setParams(focus ? { offer: focus, range: v } : { range: v }); }} options={RANGES.map((r) => ({ ...r, testId: `promotions-report-range-${r.value}` }))} />
        {focus && <Button size="sm" variant="outline" className="h-8" onClick={() => setParams({ range })} data-testid="promotions-report-all">All offers</Button>}
      </FilterBar>

      <Stats className="mb-6" data-testid="promotions-report-tiles">
        <Stat label="Bills with an offer" value={`${t.share_of_orders ?? 0}%`} hint={t.orders ? `${t.orders_with_offer} of ${t.orders}` : undefined} testId="promotions-report-share" />
        <Stat label="Given" value={formatCurrency(t.given)} hint={`${t.redemptions ?? 0} redemptions · ${t.units ?? 0} units`} testId="promotions-report-given" />
        <Stat label="Net cost" value={formatCurrency(t.net_cost)} hint={t.funded ? `${formatCurrency(t.funded)} funded by suppliers` : 'nothing supplier-funded'} />
        <Stat label="Offers used" value={t.offers_used ?? 0} hint={offers.find((o) => !o.redemptions) ? `${offers.filter((o) => !o.redemptions).length} ran and were never used` : undefined} />
      </Stats>

      <h2 className={SECTION}>Did it work</h2>
      <DataTable columns={offerColumns} rows={offers} rowKey="id" loading={loading} defaultSort={{ key: 'given', dir: 'desc' }}
        empty={{ word: 'Promotions', title: 'No offers in this period', action: <Button variant="outline" asChild><Link to="/back-office/promotions">Set one up</Link></Button> }}
        testId="promotions-report-offers" className="mb-3" />
      {focused && (
        <div className="mb-8 grid gap-4 lg:grid-cols-[1fr_360px]">
          <div className="rounded-lg border border-hairline bg-white px-4 py-3">
            <p className="text-[12.5px] text-ink-soft">
              {focused.uplift?.pct != null
                ? <>Its items sold <b className="text-ink">{focused.uplift.units_per_day_on}</b> a day when it ran, <b className="text-ink">{focused.uplift.units_per_day_off}</b> against {focused.uplift.against} ({focused.uplift.comparison_days} days compared).</>
                : <>No clean uplift figure: {focused.uplift?.note}.</>}
              {focused.items?.length ? <> Items: {focused.items.join(', ')}.</> : null}
            </p>
          </div>
          <Insight
            compact
            purpose="Did it pay for itself?"
            read={() => promotionsApi.reportInsight({ offer: focused, start: data?.start, end: data?.end, site })}
            testId="promotions-report-insight"
          />
        </div>
      )}
      {!focused && <p className="mb-8 text-[12px] text-ink-faint">Uplift compares the offer's items per trading day when it ran against the same weekdays it didn't or, for an offer that always runs on those days, against the days it doesn't. Click an offer for its own view.</p>}

      <h2 className={SECTION}>Over time</h2>
      <div className="mb-8 rounded-lg border border-hairline bg-white px-4 py-3" data-testid="promotions-report-daily">
        {data?.daily?.length ? <DailyBars daily={data.daily} focus={focus} /> : <p className="text-sm text-ink-faint">Nothing yet.</p>}
        <p className="mt-1 text-[12px] text-ink-faint">Redemptions per trading day. A faint bar is a day {focus ? 'this offer' : 'no offer'} was scheduled: quiet because it wasn't running, not because it didn't work.</p>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className={SECTION}>By rate</h2>
          <DataTable columns={rateColumns} rows={data?.by_rate || []} rowKey="rate" loading={loading} dense defaultSort={{ key: 'given', dir: 'desc' }}
            empty={{ word: 'Rates', title: 'No offers used' }} testId="promotions-report-rates" />
        </div>
        <div>
          <h2 className={SECTION}>Hand discounts, by rate</h2>
          {(data?.hand_discounts_by_rate || []).length === 0 && !loading
            ? <Empty word="Discounts" title="No hand discounts">Everything given was an offer the office set up.</Empty>
            : <DataTable columns={handColumns} rows={data?.hand_discounts_by_rate || []} rowKey="rate" loading={loading} dense testId="promotions-report-hand" />}
          <p className="mt-2 text-[12px] text-ink-faint">Discretion beside policy. The full trail is on <Link to="/back-office/reports/ops/discounts-refunds" className="text-verlo-hover underline">Discounts &amp; Refunds</Link>.</p>
        </div>
      </div>

      <div className="mb-2 flex items-baseline justify-between">
        <h2 className={SECTION}>By item</h2>
        <Button size="sm" variant="ghost" className="h-8" onClick={downloadItems} disabled={!data?.by_item?.length} data-testid="promotions-report-csv"><Download size={14} className="mr-1.5" />CSV</Button>
      </div>
      <DataTable columns={itemColumns} rows={data?.by_item || []} rowKey="menu_item_id" loading={loading} dense defaultSort={{ key: 'units_on', dir: 'desc' }}
        empty={{ word: 'Items', title: 'No promoted items' }} testId="promotions-report-items" className="mb-8" />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className={SECTION}>{focused?.kind === 'competition' ? 'Leaderboard' : 'By seller'}</h2>
          <DataTable columns={sellerColumns} rows={sellers} rowKey="id" loading={loading} dense defaultSort={{ key: 'units', dir: 'desc' }}
            empty={{ word: 'Sellers', title: 'Nobody yet' }} testId="promotions-report-sellers" />
          {(data?.competitions || []).length > 0 && !focused && (
            <p className="mt-2 text-[12px] text-ink-faint">Competitions: {data.competitions.map((c) => <button key={c.id} type="button" className="text-verlo-hover underline mr-2" onClick={() => setParams({ offer: c.id, range })}>{c.name}</button>)}</p>
          )}
        </div>
        <div>
          <h2 className={SECTION}>By site</h2>
          <DataTable columns={siteColumns} rows={sites} rowKey="site_id" loading={loading} dense defaultSort={{ key: 'given', dir: 'desc' }}
            empty={{ word: 'Sites', title: 'Nothing yet' }} testId="promotions-report-sites" />
        </div>
      </div>
    </ReportsLayout>
  );
};

export default PromotionsReportPage;
